import { Box, Checkbox, FormControl, Stack, Typography } from "@mui/material";
import { isNil } from "lodash-es";
import { useMemo, useState } from "react";

import { ObjectHelper } from "@/common/helpers/object";
import { ApiEnumName } from "@/common/services/enum";
import { VehicleBodyType, VehicleFuelType, VehicleSize } from "@/core/api/generated/v1";

import AppButton from "../../Button/AppButton";
import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import AppPopoverActions from "../../Popover/AppPopoverActions";
import AppTooltip from "../../Tooltip/AppTooltip";
import VehicleGenerationAutocomplete from "../ReferenceData/VehicleGeneration/VehicleGenerationAutocomplete";
import VehicleMakeAutocomplete from "../ReferenceData/VehicleMake/VehicleMakeAutocomplete";
import VehicleModelAutocomplete from "../ReferenceData/VehicleModel/VehicleModelAutocomplete";
import VehicleModificationAutocomplete from "../ReferenceData/VehicleModification/VehicleModificationAutocomplete";
import VehiclePartTypeAutocompleteV2 from "../ReferenceData/VehiclePartType/VehiclePartTypeAutocompleteV2";

/** Unset keys are considered as `false`. */
type DisplayProps = {
  [Key in keyof RepairSparePartAutocompleteCustomFiltersValues]: boolean;
};
const defaultDisplayProps: DisplayProps = {
  partTypeId: true,
  vehicleSize: true,
  bodyType: true,
  fuelType: true,
  makeId: true,
  modelId: true,
  generationId: false,
  modificationId: false,
};

/** Unset keys are considered as `false`. */
export type RepairSparePartAutocompleteCustomFiltersValuesEnablementMap = {
  [Key in keyof RepairSparePartAutocompleteCustomFiltersValues]: Nil<boolean>;
};
const _defaultEnablementMap: RepairSparePartAutocompleteCustomFiltersValuesEnablementMap = {
  partTypeId: false,
  vehicleSize: false,
  bodyType: false,
  fuelType: false,
  makeId: false,
  modelId: false,
  generationId: false,
  modificationId: false,
};

export interface RepairSparePartAutocompleteCustomFiltersValues {
  partTypeId?: string;
  vehicleSize?: VehicleSize;
  bodyType?: VehicleBodyType;
  fuelType?: VehicleFuelType;
  makeId?: string;
  modelId?: string;
  generationId?: string;
  modificationId?: string;
  suggestedIds?: string[];
  restrictedIds?: string[];

  // memory for state preservation (internal use)
  _suggestedIdsMemory?: string[];
  _restrictedIdsMemory?: string[];
}

export interface RepairSparePartAutocompleteCustomFilters {
  enablementMap: RepairSparePartAutocompleteCustomFiltersValuesEnablementMap;
  /** Optimized for storage size. */
  enablementMapForPersistence: RepairSparePartAutocompleteCustomFiltersValuesEnablementMap;
  /** Ignoring enablement */
  values: Nil<RepairSparePartAutocompleteCustomFiltersValues>;
  /** Considering enablement. */
  valuesEnabled: Nil<RepairSparePartAutocompleteCustomFiltersValues>;
  /** Ignoring enablement */
  count: number;
  /** Considering enablement. */
  countEnabled: number;
  /** Ignoring enablement */
  isAny: boolean;
  /** Considering enablement. */
  isAnyEnabled: boolean;
}

export interface RepairSparePartAutocompleteCustomFiltersInputProps {
  searchFilters: any;
  defaultFilters?: Partial<RepairSparePartAutocompleteCustomFiltersValues> | null;
  defaultEnablementMap?: Partial<RepairSparePartAutocompleteCustomFiltersValuesEnablementMap> | null;
  displayProps?: Partial<DisplayProps>;
  onSave: (newValue: RepairSparePartAutocompleteCustomFilters | null) => void;
  onCancel: () => void;
}

function isPrivateStateKey(key: keyof RepairSparePartAutocompleteCustomFiltersValues) {
  return key.startsWith("_");
}

// function isResettableStateKey(key: keyof RepairSparePartAutocompleteCustomFiltersValues) {
//   return !isPrivateStateKey(key) && key !== "restrictedIds";
// }

function isKeyEnabled(params: {
  enablementMap: Nil<RepairSparePartAutocompleteCustomFiltersValuesEnablementMap>;
  key: keyof RepairSparePartAutocompleteCustomFiltersValues | string;
}): boolean {
  return (
    !!params.enablementMap &&
    params.enablementMap[params.key as keyof RepairSparePartAutocompleteCustomFiltersValues] ===
      true
  );
}

export function computeCustomFilters(params: {
  enablementMap: Nil<RepairSparePartAutocompleteCustomFiltersValuesEnablementMap>;
  values: Nil<RepairSparePartAutocompleteCustomFiltersValues>;
}): RepairSparePartAutocompleteCustomFilters {
  const enablementMap = params.enablementMap || _defaultEnablementMap;
  const count = params.values
    ? Object.entries(params.values).filter(
        ([key, value]) =>
          !isPrivateStateKey(key as keyof RepairSparePartAutocompleteCustomFiltersValues) &&
          !isNil(value),
      ).length
    : 0;
  const enabledCount = params.values
    ? Object.entries(params.values).filter(
        ([key, value]) =>
          !isPrivateStateKey(key as keyof RepairSparePartAutocompleteCustomFiltersValues) &&
          !isNil(value) &&
          isKeyEnabled({ enablementMap, key }),
      ).length
    : 0;
  return {
    enablementMap: enablementMap,
    enablementMapForPersistence: ObjectHelper.resetValues(enablementMap, undefined, (key, value) =>
      isNil(value),
    ),
    values: params.values,
    valuesEnabled: params.values
      ? ObjectHelper.resetValues(
          params.values,
          undefined,
          (key) => !isKeyEnabled({ enablementMap, key }),
        )
      : undefined,
    count: count,
    countEnabled: enabledCount,
    isAny: count > 0,
    isAnyEnabled: enabledCount > 0,
  };
}

export function RepairSparePartAutocompleteCustomFiltersInput({
  searchFilters,
  defaultFilters,
  defaultEnablementMap,
  displayProps,
  onSave,
  onCancel,
}: RepairSparePartAutocompleteCustomFiltersInputProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const initialEnablementMap = useMemo<RepairSparePartAutocompleteCustomFiltersValuesEnablementMap>(
    () => ({
      ..._defaultEnablementMap,
      ...defaultEnablementMap,
    }),
    [defaultEnablementMap],
  );
  const initialValues = useMemo<RepairSparePartAutocompleteCustomFiltersValues | null>(
    () =>
      defaultFilters
        ? {
            ...defaultFilters,
            _suggestedIdsMemory: defaultFilters._suggestedIdsMemory || defaultFilters.suggestedIds,
            _restrictedIdsMemory:
              defaultFilters._restrictedIdsMemory || defaultFilters.restrictedIds,
          }
        : null,
    [defaultFilters],
  );

  const [enablementMap, setEnablementMap] =
    useState<RepairSparePartAutocompleteCustomFiltersValuesEnablementMap>(initialEnablementMap);
  const [values, setValues] = useState<RepairSparePartAutocompleteCustomFiltersValues | null>(
    initialValues,
  );

  const filtersComputed = useMemo<RepairSparePartAutocompleteCustomFilters>(
    () => computeCustomFilters({ enablementMap: enablementMap, values }),
    [enablementMap, values],
  );

  // console.log("RepairSparePartAutocompleteCustomFiltersInput", {
  //   defaultFilters,
  //   defaultEnablementMap,
  //   enablementMap,
  //   values,
  //   filtersComputed,
  // });

  const reset = () => {
    // disable all filters and reset to initial values
    setEnablementMap(ObjectHelper.resetValues(enablementMap, false, () => true));
    setValues(initialValues);
    // setValues(
    //   values
    //     ? ObjectHelper.resetValues(values, undefined, (key) => isResettableStateKey(key))
    //     : null,
    // );
  };

  const save = () => {
    onSave(filtersComputed);
    reset();
  };

  const cancel = () => {
    onCancel();
    reset();
  };

  return (
    <Box>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>
        Filters
      </Typography>

      <Box>
        {displayProps.partTypeId && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable part type filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.partTypeId ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, partTypeId: e.target.checked }))
                  }
                />
              </AppTooltip>

              <VehiclePartTypeAutocompleteV2
                entityId={values?.partTypeId}
                isPreload={false}
                size='small'
                disabled={!enablementMap.partTypeId}
                onChange={(newValue) => {
                  setValues((prev) => ({ ...prev, partTypeId: newValue?.id || undefined }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.makeId && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable make filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.makeId ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, makeId: e.target.checked }))
                  }
                />
              </AppTooltip>

              <VehicleMakeAutocomplete
                entityId={values?.makeId}
                isPreload={false}
                size='small'
                disabled={!enablementMap.makeId}
                onChange={(newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    makeId: newValue?.id || undefined,
                    modelId: undefined,
                    generationId: undefined,
                    modificationId: undefined,
                  }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.modelId && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable model filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.modelId ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, modelId: e.target.checked }))
                  }
                />
              </AppTooltip>

              <VehicleModelAutocomplete
                entityId={values?.modelId}
                isPreload={false}
                size='small'
                disabled={!enablementMap.modelId}
                searchFilters={{
                  makeId: values?.makeId,
                }}
                onChange={(newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    modelId: newValue?.id || undefined,
                    generationId: undefined,
                    modificationId: undefined,
                  }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.vehicleSize && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable vehicle size filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.vehicleSize ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, vehicleSize: e.target.checked }))
                  }
                />
              </AppTooltip>

              <ApiEnumAutocomplete
                type={ApiEnumName.VehicleSize}
                value={values?.vehicleSize}
                label='Size'
                size='small'
                disabled={!enablementMap.vehicleSize}
                onChange={(newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    vehicleSize: newValue || undefined,
                  }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.bodyType && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable body type filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.bodyType ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, bodyType: e.target.checked }))
                  }
                />
              </AppTooltip>

              <ApiEnumAutocomplete
                type={ApiEnumName.VehicleBodyType}
                value={values?.bodyType}
                label='Body type'
                size='small'
                disabled={!enablementMap.bodyType}
                onChange={(newValue) => {
                  setValues((prev) => ({ ...prev, bodyType: newValue || undefined }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.fuelType && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable fuel type filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.fuelType ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, fuelType: e.target.checked }))
                  }
                />
              </AppTooltip>

              <ApiEnumAutocomplete
                type={ApiEnumName.VehicleFuelType}
                value={values?.fuelType}
                label='Fuel type'
                size='small'
                disabled={!enablementMap.fuelType}
                onChange={(newValue) => {
                  setValues((prev) => ({ ...prev, fuelType: newValue || undefined }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.generationId && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable generation filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.generationId ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, generationId: e.target.checked }))
                  }
                />
              </AppTooltip>

              <VehicleGenerationAutocomplete
                entityId={values?.generationId}
                isPreload={false}
                size='small'
                disabled={!enablementMap.generationId}
                searchFilters={{
                  makeId: values?.makeId,
                  modelId: values?.modelId,
                }}
                onChange={(newValue) => {
                  setValues((prev) => ({
                    ...prev,
                    generationId: newValue?.id || undefined,
                    modificationId: undefined,
                  }));
                }}
              />
            </Stack>
          </FormControl>
        )}

        {displayProps.modificationId && (
          <FormControl margin='dense' fullWidth>
            <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
              <AppTooltip title='Enable modification filter' isDefaultEnterDelays>
                <Checkbox
                  checked={enablementMap.modificationId ?? false}
                  edge='start'
                  onChange={(e) =>
                    setEnablementMap((prev) => ({ ...prev, modificationId: e.target.checked }))
                  }
                />
              </AppTooltip>

              <VehicleModificationAutocomplete
                entityId={values?.modificationId}
                isPreload={false}
                size='small'
                disabled={!enablementMap.modificationId}
                searchFilters={{
                  makeId: values?.makeId,
                  modelId: values?.modelId,
                  generationId: values?.generationId,
                }}
                onChange={(newValue) => {
                  setValues((prev) => ({ ...prev, modificationId: newValue?.id || undefined }));
                }}
              />
            </Stack>
          </FormControl>
        )}
      </Box>

      <AppPopoverActions
        sx={{ mt: 2 }}
        primaryActions={
          <>
            <AppButton variant='outlined' color='text' onClick={cancel}>
              Cancel
            </AppButton>
            <AppButton variant='contained' color='primary' onClick={save}>
              Save
            </AppButton>
          </>
        }
        secondaryActions={
          <>
            <AppButton variant='text' color='text' onClick={reset}>
              Reset
            </AppButton>
          </>
        }
      />
    </Box>
  );
}
